<script lang="ts">
  import axios from 'axios';
  import { onMount } from 'svelte';
  import { couleursDeNiveau, niveauxMaturite } from '../niveaux-maturite/NiveauxMaturite.donnees';
  import type { IdNiveau } from '../niveaux-maturite/NiveauxMaturite.type';
  import { profilStore } from '../stores/profil.store';
  import { fabriqueFilAriane, type PropriétésFilAriane } from '../ui/filAriane';
  import Heros from '../ui/Heros.svelte';
  import IllustrationDragonPasDeResultat from '../ui/IllustrationDragonPasDeResultat.svelte';
  import Lien from '../ui/Lien.svelte';
  import GraphiqueAnneau from './GraphiqueAnneau.svelte';
  import LegendeAnneau from './LegendeAnneau.svelte';
  import LegendeRadarSessionGroupe from './LegendeRadarSessionGroupe.svelte';
  import RadarSessionGroupe from './RadarSessionGroupe.svelte';
  import type { RéponsesResultatTest } from './ResultatsTest.type';
  import { type Serie, type SerieRadar } from './Serie';
  import TuilesMaturiteSessionGroupe from './TuilesMaturiteSessionGroupe.svelte';

  type ResumeNiveau = {
    total: number;
    moyennes: RéponsesResultatTest;
  };
  type ResultatsSessionGroupe = {
    nombreParticipants: number;
    resume: Record<IdNiveau, ResumeNiveau>;
  };

  let serie: Serie = $state([]);
  let resultatsSessionGroupe: ResultatsSessionGroupe | undefined = $state();

  let seriesRadar: SerieRadar[] = $state([]);

  async function rechargeResultatsGroupe() {
    const codeSessionGroupe = new URLSearchParams(window.location.search).get('code');
    const reponse = await axios.get<ResultatsSessionGroupe>(`/api/sessions-groupe/${codeSessionGroupe}/resultats`);
    const nouveauxResultats = reponse.data;
    resultatsSessionGroupe = nouveauxResultats;
    serie = niveauxMaturite.map((niveau) => ({
      libelle: niveau.label,
      valeur: nouveauxResultats.resume[niveau.id].total,
    }));
    seriesRadar = niveauxMaturite.map((niveau) => ({
      id: niveau.id,
      valeurs: nouveauxResultats.resume[niveau.id].moyennes,
      couleur: couleursDeNiveau[niveau.id],
    }));
  }

  onMount(() => {
    let intervalle: ReturnType<typeof setInterval> | undefined;
    const démarreActualisation = async () => {
      await rechargeResultatsGroupe();
      intervalle = setInterval(rechargeResultatsGroupe, 5000);
    };

    void démarreActualisation();
    return () => {
      if (intervalle) clearInterval(intervalle);
    };
  });
  const propriétésFilAriane: PropriétésFilAriane = {
    branche: {
      nom: 'Test de maturité cyber',
      lien: '/test-maturite/',
    },
    feuille: 'Session de groupe',
    fondSombre: true,
  };
</script>

<Heros
  description="Ce résultat nous permet de vous guider et de vous fournir les informations et les outils essentiels pour agir sur votre maturité cyber."
  format="banniere"
  segmentsFilAriane={fabriqueFilAriane(propriétésFilAriane, !!$profilStore)}
  theme="clair"
  titre="Résultat de maturité cyber"
/>

{#if resultatsSessionGroupe && resultatsSessionGroupe.nombreParticipants > 0}
  <dsfr-container>
    <div class="contenu-section">
      <h2>Les 5 niveaux de maturité cyber</h2>
      <TuilesMaturiteSessionGroupe />
      <Lien href="/niveaux-maturite" blank libelle="Les niveaux de maturité cyber"></Lien>
    </div>
  </dsfr-container>

  <dsfr-container>
    <div class="contenu-section">
      <h2>Répartition des niveaux de maturité cyber de cette session</h2>
      <div class="repartition-niveaux-maturite">
        {#if resultatsSessionGroupe && resultatsSessionGroupe.nombreParticipants > 0}
          <GraphiqueAnneau {serie} nomDeLaDonnee="Participants" />
          <LegendeAnneau {serie} />
        {:else}
          <div>Pas de résultat, rechargez la page</div>
        {/if}
      </div>
    </div>
  </dsfr-container>

  <dsfr-container>
    <div class="contenu-section repartition-reponses">
      <h2>Répartition des réponses</h2>
      <RadarSessionGroupe series={seriesRadar} />
      <LegendeRadarSessionGroupe />
      <div class="message-information texte-mention-xs">
        Le résultat obtenu est une évaluation indicative basée sur un modèle élaboré par l’ANSSI. La maturité cyber
        n’est pas une évaluation du niveau de sécurité des systèmes d’information d’une organisation mais de sa posture
        à l’égard des enjeux cyber.
      </div>
    </div>
  </dsfr-container>
{:else}
  <dsfr-container>
    <div class="contenu-section contenu-sans-resultat">
      <IllustrationDragonPasDeResultat texteAlternatif="Pas encore de résultat soumis par les participants" />
      <h4>Encouragez vos participants à finaliser le test</h4>
      <p>
        Les résultats seront disponibles dès que les participants auront complété le test.<br /> Invitez-les à le finaliser.
      </p>
    </div>
  </dsfr-container>
{/if}

<style lang="scss">
  @use '../../../assets/styles/responsive' as *;

  dsfr-container {
    padding: 48px 0 0;

    .contenu-section {
      display: flex;
      flex-direction: column;
      gap: 32px;
      padding-bottom: 48px;
      border-bottom: 1px solid var(--border-default-grey);
    }
  }

  h2 {
    margin: 0;
    font-size: 2rem;
    line-height: 2.5rem;
  }

  .repartition-niveaux-maturite {
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    gap: 48px;
  }

  .repartition-reponses {
    h2 {
      margin-bottom: 32px;
    }
  }

  .message-information {
    padding: 24px 0;
    margin-bottom: 0;
  }

  .contenu-section {
    &.contenu-sans-resultat {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      margin: 32px auto 72px;
      border-bottom: none;

      h4 {
        font-size: 1.5rem;
        line-height: 2rem;
        text-align: center;
        margin: 0;
      }

      p {
        text-align: center;
        margin: 0;
      }
    }
  }
</style>
